import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import {
    GRID_CHECKBOX_SELECTION_FIELD,
    type GridPinnedColumnFields
} from '../lib/dataGrid';

/**
 * Column pinning shared by the office grids, which scroll sideways: the row's
 * identifier is pinned left so a scrolled row is still attributable, `actions`
 * right because it is last and would otherwise be the first thing out of reach.
 * The field grid uses neither — it is phone-first.
 */

/** For a grid with no one identifying column. */
export const PINNED_ACTIONS = { right: ['actions'] };

/**
 * Actions right, plus `identifierField` left (Maßnahmennummer, Befundnummer,
 * …). Call it at MODULE level: `pinnedColumns` is controlled, so a fresh object
 * every render would churn the grid's state.
 */
export function pinnedActionsWith(identifierField: string) {
    return { left: [identifierField], right: ['actions'] };
}

/**
 * The same, with the selection checkboxes pinned OUTSIDE the identifier: a tick
 * has to travel with its row, and it belongs left of the identifier.
 *
 * Several identifiers where a grid needs them — the Auswertung pins the Projekt
 * beside the record's number, which alone does not say which record a row is.
 */
export function pinnedSelectionWith(...identifierFields: string[]) {
    return {
        left: [GRID_CHECKBOX_SELECTION_FIELD, ...identifierFields],
        right: ['actions']
    };
}

/** Nothing pinned, one object — a new one per render would churn grid state. */
const NOTHING_PINNED: GridPinnedColumnFields = {};

/**
 * The pinning a grid should START with on THIS device: what it asked for above
 * `md`, nothing below. On a narrow screen a pinned column costs its width
 * twice — taken out of the scrollable area AND held there — leaving a sliver
 * between identifier and actions for everything the reader wants.
 *
 * Pass the result where the grid takes its `initialState`, so a column can
 * still be pinned by hand. Being INITIAL state it is read once: a device
 * rotated across the breakpoint keeps what it started with.
 */
export function useGridPinning(
    pinned: GridPinnedColumnFields
): GridPinnedColumnFields {
    const theme = useTheme();
    const narrow = useMediaQuery(theme.breakpoints.down('md'));
    return narrow ? NOTHING_PINNED : pinned;
}
